import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  ChevronLeft, User, Edit, FileText, BarChart3, 
  AlertCircle, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PlayerEvaluationForm from '@/components/PlayerEvaluationForm';
import PlayerProfileView from '@/components/PlayerProfileView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AthleteEvaluationPage
 * Complete evaluation management page for coaches
 */
const AthleteEvaluationPage = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'evaluate'
  const [athlete, setAthlete] = useState(null);
  const [evaluations, setEvaluations] = useState({ private: null, team: null });
  const [loading, setLoading] = useState(true);
  const [coachType, setCoachType] = useState('private');

  useEffect(() => {
    if (athleteId) {
      fetchAthleteData();
      fetchEvaluations();
    }
  }, [athleteId]);

  const fetchAthleteData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athleteId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAthlete(data);
      }
    } catch (error) {
      console.error('Error fetching athlete:', error);
    }
  };

  const fetchEvaluations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/evaluations/athlete/${athleteId}`);
      const data = await response.json();
      setEvaluations({
        private: data.private_evaluation,
        team: data.team_evaluation
      });
    } catch (error) {
      console.error('Error fetching evaluations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEvaluationSaved = () => {
    fetchEvaluations();
    setActiveTab('profile');
  };

  // Determine coach type based on relationship
  // For now, default to private coach
  const isPrivateCoach = user?.role === 'coach';
  const isTeamCoach = false; // Would be determined by team membership

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft size={20} />
            Back
          </button>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary/20 rounded-full flex items-center justify-center">
                <User size={28} className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading">{athlete?.name || 'Athlete'}</h1>
                <p className="text-muted-foreground">Player Evaluation & Development Profile</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-border pb-2">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'profile' 
                ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <BarChart3 size={18} />
            Player Profile
          </button>
          <button
            onClick={() => setActiveTab('evaluate')}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition-colors ${
              activeTab === 'evaluate' 
                ? 'bg-primary/20 text-primary border-b-2 border-primary' 
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Edit size={18} />
            Evaluate
          </button>
        </div>

        {/* Content */}
        {activeTab === 'profile' && (
          <div>
            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div 
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  evaluations.private 
                    ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20' 
                    : 'bg-muted/30 border-border hover:bg-muted/50'
                }`}
                onClick={() => { setCoachType('private'); setActiveTab('evaluate'); }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Private Coach Evaluation</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Technical execution, individual drills, coordination
                    </p>
                  </div>
                  {evaluations.private ? (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                      Completed
                    </span>
                  ) : (
                    <Plus size={20} className="text-muted-foreground" />
                  )}
                </div>
              </div>

              <div 
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  evaluations.team 
                    ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500/20' 
                    : 'bg-muted/30 border-border hover:bg-muted/50'
                }`}
                onClick={() => { setCoachType('team'); setActiveTab('evaluate'); }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Team Coach Evaluation</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Match performance, tactics, decision making
                    </p>
                  </div>
                  {evaluations.team ? (
                    <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs">
                      Completed
                    </span>
                  ) : (
                    <Plus size={20} className="text-muted-foreground" />
                  )}
                </div>
              </div>
            </div>

            {/* Profile View */}
            <PlayerProfileView 
              athleteId={athleteId}
              athleteName={athlete?.name}
              showRadar={true}
            />
          </div>
        )}

        {activeTab === 'evaluate' && (
          <div>
            {/* Coach Type Selector */}
            <div className="mb-6 flex gap-3">
              <button
                onClick={() => setCoachType('private')}
                className={`flex-1 p-4 rounded-lg border transition-colors ${
                  coachType === 'private'
                    ? 'bg-primary/20 border-primary'
                    : 'bg-muted/30 border-border hover:bg-muted/50'
                }`}
              >
                <h3 className="font-medium">Private Coach</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Training & technical assessment
                </p>
              </button>
              <button
                onClick={() => setCoachType('team')}
                className={`flex-1 p-4 rounded-lg border transition-colors ${
                  coachType === 'team'
                    ? 'bg-primary/20 border-primary'
                    : 'bg-muted/30 border-border hover:bg-muted/50'
                }`}
              >
                <h3 className="font-medium">Team Coach</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Match & tactical assessment
                </p>
              </button>
            </div>

            {/* Evaluation Form */}
            <PlayerEvaluationForm
              athleteId={athleteId}
              athleteName={athlete?.name || 'Athlete'}
              coachId={user?.id}
              coachName={user?.full_name || user?.name}
              coachType={coachType}
              existingEvaluation={coachType === 'private' ? evaluations.private : evaluations.team}
              onSave={handleEvaluationSaved}
              onCancel={() => setActiveTab('profile')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AthleteEvaluationPage;
